// AnnotatedBlock — the LIVE select→annotate surface over one reading
// block's text (.claude/analysis/pdf-source-view-sota.md §3, the SOTA
// adoption this build wires end to end). Promotes the sketch's rendering
// (`sketches/source-view-annotation/SourceViewAnnotationSketch.tsx`) from
// a static demo into the real `SourceView`.
//
// SCOPE, stated once: text-span only (no bbox/`SpatialSelector` region
// annotation — anchor.ts defers it), Highlight + Comment only (no
// Correction — core has no `PlaceCorrection` command yet), single-text-
// node selection math (a block with prior marks still resolves offsets
// correctly via a `TreeWalker`, but a selection that starts/ends inside
// the FLOATING TOOLBAR itself is guarded against, not a reading-pane
// edge case). "Add to context" anchors the resource's OWN tab context
// (`meta.sourceId` doubling as both `anchor` and `ref` — see the header
// note below) rather than whatever OTHER tab the author may be working
// in; wiring the richer "the author's active surface" anchor needs
// `AppShellHost`'s `activeContextAnchor`, out of this build's file scope
// (constraint: do not touch `AppShellHost.tsx`) — named as a follow-up,
// not silently approximated.
//
// PORTS ACCESS: every other `*Host` component in this codebase receives
// `ports: AppPorts` as an explicit prop from `AppShellHost` (the app's
// one `useAppPorts()` call site). This component is the ONE deliberate
// exception: `AppShellHost.tsx` is out of scope for this build (parallel
// work owns it this round), so there is no call site available to thread
// a new prop through. `useAppPorts()` is still the SAME sanctioned
// context-injection seam every other consumer uses — not a raw side-
// effect reach — so this stays a second, explicitly-justified consumer
// of that one context, not a violation of pure-core/impure-shell.
//
// KEYBOARD PATH: a mouse drag-select is the primary gesture (tracked via
// `selectionchange`-driven `mouseup`/`keyup` checks against a `TreeWalker`
// offset map). Making every paragraph a tab stop to support keyboard
// selection would flood the tab order of a long document, so the
// keyboard-accessible entry point is instead a per-block "Select this
// passage" button (reveals on hover/focus beside the existing "Add note"
// button) that opens the SAME toolbar anchored on the block's WHOLE text
// — a natively focusable, Enter/Space-activatable control, not a fake
// affordance. A live text selection (however achieved) still narrows the
// anchor to the precise span when present.

export type { AnnotatedBlockProps };
export { AnnotatedBlock };

import * as Core from "@bilda/core";
import { Result } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { type KeyboardEvent, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppPorts } from "../../app/ports.js";
import { Badge } from "../Badge/Badge.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Tooltip, TooltipProvider } from "../Tooltip/Tooltip.js";
import { SelectionToolbar } from "./SelectionToolbar.js";
import { markRuns, spanFromSelection } from "./selectionSpan.js";
import type { SelectedSpan, SourceAnnotation } from "./types.js";

type AnnotatedBlockProps = {
    readonly blockId: string;
    readonly text: string;
    // Undefined ⇒ no resource id known (a caller/story that omitted
    // `meta.sourceId`) — the new gesture affordances stay hidden; the
    // block still renders its plain text.
    readonly sourceId: string | undefined;
    // Annotations already resolved to THIS block by the caller
    // (`AnnotatedReading` below), server truth as of the last load.
    readonly annotations: readonly SourceAnnotation[];
};

// A resource ref, doubling as both the "Add to context" anchor (this
// source's own tab context) and the ref being introduced into it — see
// the header note on the self-reference scoping shortcut.
const sourceRef = (id: string): Core.Resource.Ref.Ref =>
    Core.Resource.Ref.mkWhole(
        Core.Resource.Id.mk(id),
        Core.Versioning.Version.v1,
    );

// Absolute character offset of a DOM position within `root`'s flattened
// text content — walks every text node in document order, summing
// lengths, so a block with existing `<mark>` spans still resolves
// correctly (offsets are NOT assumed to land in one single text node).
const textOffset = (root: Node, node: Node, offset: number): number => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let total = 0;
    for (
        let current = walker.nextNode();
        current !== null;
        current = walker.nextNode()
    ) {
        if (current === node) return total + offset;
        total += (current.textContent ?? "").length;
    }
    return total;
};

type PendingSelection = {
    readonly span: SelectedSpan;
    readonly top: number;
    readonly left: number;
};

type ToolbarMode = "actions" | "comment";

const AnnotatedBlock = ({
    blockId,
    text,
    sourceId,
    annotations,
}: AnnotatedBlockProps) => {
    const { t } = useLingui();
    const ports = useAppPorts();
    const containerRef = useRef<HTMLSpanElement>(null);
    const [pending, setPending] = useState<PendingSelection | null>(null);
    const [toolbarMode, setToolbarMode] = useState<ToolbarMode>("actions");
    const [commentDraft, setCommentDraft] = useState("");
    const [busy, setBusy] = useState(false);
    // Optimistic local additions, merged with the server-truth `annotations`
    // prop — the host reloads the source after a successful place, at which
    // point these are superseded by the real projection (feel-fast: the
    // mark appears the instant the port resolves, not on the next reload).
    const [optimistic, setOptimistic] = useState<readonly SourceAnnotation[]>(
        [],
    );

    const shown = [...annotations, ...optimistic];
    const quotes = shown.flatMap((a) =>
        a.quote === undefined ? [] : [a.quote],
    );

    const dismiss = () => {
        setPending(null);
        setToolbarMode("actions");
        setCommentDraft("");
    };

    const checkSelection = () => {
        const sel = window.getSelection();
        const container = containerRef.current;
        if (sel === null || container === null) return;
        if (sel.isCollapsed || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        if (
            !container.contains(range.startContainer) ||
            !container.contains(range.endContainer)
        ) {
            return;
        }
        const a = textOffset(
            container,
            range.startContainer,
            range.startOffset,
        );
        const b = textOffset(container, range.endContainer, range.endOffset);
        const span = spanFromSelection(text, Math.min(a, b), Math.max(a, b));
        if (span === undefined) return;
        // Viewport-relative (not container-relative): the toolbar PORTALS
        // to `document.body` (see the render below) so its `<div>` never
        // nests inside this block's `<p>` — HTML forbids a block element
        // as a `<p>`'s descendant, and the reading canvas renders every
        // block inside one. `position: fixed` + the raw
        // `getBoundingClientRect()` values is the correct pairing for a
        // portalled overlay; re-anchoring on scroll is a stated MVP
        // shortcut (the toolbar dismisses on the next selection change or
        // Escape, same as today).
        const rect = range.getBoundingClientRect();
        setPending({ span, top: rect.top, left: rect.left });
        setToolbarMode("actions");
    };

    const openForWholeBlock = () => {
        const span = spanFromSelection(text, 0, text.length);
        const container = containerRef.current;
        if (span === undefined || container === null) return;
        const rect = container.getBoundingClientRect();
        setPending({ span, top: rect.top, left: rect.left });
        setToolbarMode("actions");
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
        if (event.key === "Escape" && pending !== null) {
            event.stopPropagation();
            dismiss();
        }
    };

    // Both the mouse drag-select and the keyboard `Shift+Arrow` path
    // resolve through the SAME `window.getSelection()` read — the event
    // itself carries no data this handler needs, only its timing (a
    // selection has just settled).
    const handleSelectionEvent = () => checkSelection();

    const runPlace = async (run: () => Promise<void>) => {
        setBusy(true);
        try {
            await run();
        } finally {
            setBusy(false);
        }
    };

    const onHighlight = () => {
        if (sourceId === undefined || pending === null) return;
        void runPlace(async () => {
            const r = await ports.placeHighlight(
                sourceId,
                blockId,
                undefined,
                pending.span,
            );
            if (Result.isOk(r)) {
                setOptimistic((prev) => [
                    ...prev,
                    {
                        id: `optimistic-highlight-${Date.now()}`,
                        text: "",
                        category: "highlight",
                        blockId,
                        quote: pending.span.exact,
                    },
                ]);
                dismiss();
            }
        });
    };

    const onPostComment = () => {
        if (sourceId === undefined || pending === null) return;
        const value = commentDraft.trim();
        if (value.length === 0) return;
        void runPlace(async () => {
            const r = await ports.addSourceAnnotation(
                sourceId,
                blockId,
                value,
                pending.span,
            );
            if (Result.isOk(r)) {
                setOptimistic((prev) => [
                    ...prev,
                    {
                        id: `optimistic-comment-${Date.now()}`,
                        text: value,
                        category: "comment",
                        blockId,
                        quote: pending.span.exact,
                    },
                ]);
                dismiss();
            }
        });
    };

    // Scoped, per the SOTA doc §4.3 owner call: adds the WHOLE source to
    // its own tab's explicit context (idempotent at the domain level —
    // a second click is a harmless no-op). The richer "citation edge
    // from this exact passage" is deferred (see the header note); `quote`
    // is accepted so the gutter's per-mark entry point already has the
    // right call shape once that lands, but it is not sent anywhere yet.
    const onAddToContext = (_quote?: string) => {
        if (sourceId === undefined) return;
        void runPlace(async () => {
            const ref = sourceRef(sourceId);
            await ports.introduceToContext(ref, ref);
            dismiss();
        });
    };

    // This span is an event-DELEGATION container, not a fake control — it
    // observes `window.getSelection()` settling (mouseup/keyup bubble from
    // whichever REAL, natively-interactive descendant the reader used: a
    // text drag, the "Select this passage" button, or a toolbar button)
    // and relays Escape from the floating toolbar. It carries no
    // `onClick`/tabIndex/role of its own and grants no new keyboard
    // reachability the descendants don't already provide.
    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: see the comment above — event delegation only, no new interactivity.
        <span
            ref={containerRef}
            className="bilda-source-view__span-container"
            onMouseUp={handleSelectionEvent}
            onKeyUp={handleSelectionEvent}
            onKeyDown={handleKeyDown}
        >
            {markRuns(text, quotes).map((run, i) =>
                run.type === "plain" ? (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static run list, re-derived every render from the same text+quotes
                    <span key={i}>{run.text}</span>
                ) : (
                    <mark
                        // biome-ignore lint/suspicious/noArrayIndexKey: static run list, re-derived every render from the same text+quotes
                        key={i}
                        className="bilda-source-view__span-mark"
                    >
                        {run.text}
                    </mark>
                ),
            )}
            {sourceId !== undefined ? (
                <Button
                    variant="ghost"
                    size="sm"
                    className="bilda-source-view__select-trigger"
                    onClick={openForWholeBlock}
                >
                    <Trans>Select this passage</Trans>
                </Button>
            ) : null}
            {shown.length > 0 ? (
                <span className="bilda-source-view__span-gutter">
                    <TooltipProvider>
                        {shown.map((a) => (
                            <span
                                key={a.id}
                                className="bilda-source-view__span-gutter-item"
                            >
                                <Badge
                                    tone="neutral"
                                    icon={
                                        <Icon
                                            name={
                                                a.category === "highlight"
                                                    ? "star"
                                                    : "chat"
                                            }
                                            size="xs"
                                        />
                                    }
                                >
                                    {a.category === "highlight"
                                        ? t`Highlight`
                                        : t`Comment`}
                                </Badge>
                                {sourceId !== undefined ? (
                                    <Tooltip content={t`Add to context`}>
                                        <IconButton
                                            label={t`Add to context`}
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                onAddToContext(a.quote ?? "")
                                            }
                                        >
                                            <Icon name="pin" size="xs" />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                            </span>
                        ))}
                    </TooltipProvider>
                </span>
            ) : null}
            {pending !== null
                ? createPortal(
                      <span
                          className="bilda-source-view__selection-toolbar-anchor"
                          style={{ top: pending.top, left: pending.left }}
                      >
                          <SelectionToolbar
                              mode={toolbarMode}
                              busy={busy}
                              commentDraft={commentDraft}
                              onCommentDraftChange={setCommentDraft}
                              onHighlight={onHighlight}
                              onStartComment={() => setToolbarMode("comment")}
                              onPostComment={onPostComment}
                              onCancelComment={() => setToolbarMode("actions")}
                              onAddToContext={() => onAddToContext()}
                              onDismiss={dismiss}
                          />
                      </span>,
                      document.body,
                  )
                : null}
        </span>
    );
};
